// Header.jsx
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Logo from '../Images/Logo.svg';
import SettingIcon from '../Images/settins-icon.svg';
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotifications } from '../Context/NotificationContext';
import { useMessages } from '../Context/MessageContext';
import ResizeIcon from '../Images/resize.svg';
import FloatIcon from '../Images/float.svg';
import DragIcon from '../Images/drag.svg';
import axios from 'axios';
import { BsBell, BsChat, BsBook, BsBug, BsPerson } from 'react-icons/bs';
import { FaUser, FaChartBar, FaCog, FaEnvelope, FaLock, FaComments, FaRocket, FaSyncAlt, FaHistory } from 'react-icons/fa';
import TokenBalanceIndicator from '../Components/TokenSettings/TokenBalanceIndicator';
import FeedbackModal from '../Components/Feedback/FeedbackModal';
import { isTeamMemberAuthenticated, getTeamUser } from '../services/teamAuthService';
import { useFeature } from '../Context/CapabilityContext';

const HeaderContainer = styled.div`
  width: 100%;
  height: 5%;
  background: linear-gradient(90deg, #0f1419 0%, #1a1f2e 50%, #0f1419 100%);
  border-bottom: 1px solid rgba(100, 150, 200, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 10px 0 10px;
  position: relative;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  position: relative;
  height: 100%;
  margin-right: 0;
`;


const LogoContainer = styled.div`
  img {
    opacity: 0.6;
  }
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  margin-left: 5px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
  
  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const UserInfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  cursor: pointer;
`;

const MemberBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
  border: 1px solid rgba(139, 92, 246, 0.4);
  border-radius: 20px;
  padding: 6px 12px;
  color: #a78bfa;
  font-size: 13px;
  font-weight: 500;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const RoleBadge = styled.span`
  background: ${props => {
    switch(props.$role) {
      case 'admin': return 'rgba(234, 179, 8, 0.2)';
      case 'member': return 'rgba(59, 130, 246, 0.2)';
      case 'viewer': return 'rgba(107, 114, 128, 0.2)';
      default: return 'rgba(107, 114, 128, 0.2)';
    }
  }};
  color: ${props => {
    switch(props.$role) {
      case 'admin': return '#fbbf24';
      case 'member': return '#60a5fa';
      case 'viewer': return '#9ca3af';
      default: return '#9ca3af';
    }
  }};
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const UserTooltip = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: linear-gradient(135deg, rgba(25, 30, 50, 0.98) 0%, rgba(20, 25, 45, 0.98) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 12px;
  padding: 16px 20px;
  min-width: 260px;
  max-width: 320px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  z-index: 9999;
  opacity: ${props => props.$show ? 1 : 0};
  visibility: ${props => props.$show ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$show ? '0' : '-5px'});
  transition: all 0.2s ease;
  
  &:before {
    content: '';
    position: absolute;
    top: -6px;
    right: 20px;
    width: 12px;
    height: 12px;
    background: rgba(25, 30, 50, 0.98);
    border-left: 1px solid rgba(99, 102, 241, 0.3);
    border-top: 1px solid rgba(99, 102, 241, 0.3);
    transform: rotate(45deg);
  }
`;

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 0;
  gap: 16px;
  
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding-bottom: 10px;
    margin-bottom: 6px;
  }
`;

const TooltipLabel = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  min-width: 80px;
`;

const TooltipValue = styled.span`
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  text-align: right;
  word-break: break-word;
  line-height: 1.4;
`;

const IconContainer = styled.div`
  display: flex;
  position: relative;
  margin-left: 15px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  pointer-events: ${props => props.$clickable ? 'auto' : 'none'};
  opacity: ${props => props.$clickable ? 1 : 0.3};
  z-index: ${props => props.$isOpen ? 999999 : 1};
  justify-content: center;
  align-items: center;

  img {
    width: 24px;
    height: 24px;
    filter: brightness(0) invert(1);
  }

  &:hover img,
  &:hover svg {
    opacity: 0.85;
  }
`;

const NotificationIconWrapper = styled.div`
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 24px;
    height: 24px;
    filter: brightness(0) invert(1);
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #ff4444;
  color: white;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 12px;
  min-width: 15px;
  text-align: center;
  font-weight: 500;
  border: 2px solid #0f1419;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const NotificationDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #1a1f2e 0%, #0f1419 100%);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 8px;
  width: 300px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 99999;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  margin-top: 8px;
`;

const NotificationItem = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: flex-start;
  gap: 10px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  .icon-container {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: ${props => props.$type === 'message' ? 'rgba(53, 124, 210, 0.2)' : 'rgba(91, 192, 222, 0.2)'};
    color: ${props => props.$type === 'message' ? '#357CD2' : '#5bc0de'};
    flex-shrink: 0;
    margin-top: 2px;
  }

  .content {
    flex: 1;
    
    .title {
      font-weight: 500;
      color: white;
      margin-bottom: 4px;
    }

    .description {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.8);
      margin-bottom: 4px;
    }

    .time {
      color: rgba(255, 255, 255, 0.6);
      font-size: 12px;
    }
  }
`;

const SettingsSubmenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: linear-gradient(180deg, rgba(15, 18, 28, 0.98) 0%, rgba(10, 14, 24, 0.98) 100%);
  backdrop-filter: blur(24px);
  border-radius: 14px;
  padding: 6px;
  min-width: 200px;
  box-shadow: 
    0 10px 40px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(99, 102, 241, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  z-index: 999999;
  border: 1px solid rgba(99, 102, 241, 0.2);
  margin-top: 10px;
  overflow: hidden;
  
  /* Arrow pointing up - positioned to the right */
  &:before {
    content: '';
    position: absolute;
    top: -6px;
    right: 14px;
    width: 10px;
    height: 10px;
    background: rgba(15, 18, 28, 0.98);
    border-left: 1px solid rgba(99, 102, 241, 0.2);
    border-top: 1px solid rgba(99, 102, 241, 0.2);
    transform: rotate(45deg);
  }
`;

const MenuItem = styled.div`
  padding: 11px 14px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  transition: all 0.15s ease;
  font-size: 13px;
  font-weight: 500;
  position: relative;
  border-radius: 8px;
  margin: 2px 0;

  &:hover {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
    color: #fff;
  }
  
  &:active {
    background: rgba(99, 102, 241, 0.25);
    transform: scale(0.98);
  }

  span {
    color: inherit;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, rgba(99, 102, 241, 0.2) 50%, transparent 100%);
  margin: 6px 8px;
`;

const Menu = styled.div`
  width: 100%;
  background: transparent;
  border-radius: 10px;
  padding: 4px;
`;

const CenterControls = styled.div`
  display: flex;
  gap: 12px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 999;
`;

const ControlButton = styled.button`
  background: linear-gradient(135deg, #1a1f2e 0%, #0f1419 100%);
  border: 1px solid rgba(100, 150, 200, 0.2);
  border-radius: 6px;
  padding: 8px;
  margin: 0 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(100, 150, 200, 0.15);
    border-color: rgba(100, 150, 200, 0.3);
  }

  &.active {
    background: rgba(99, 102, 241, 0.2);
    border: 2px solid #6366f1;
    box-shadow: 0 0 8px rgba(99, 102, 241, 0.4);
  }

  img {
    width: 20px;
    height: 20px;
    filter: invert(1) opacity(0.9);
    
    &.disabled {
      opacity: 0.7;
    }

    &.enabled {
      filter: invert(65%) sepia(72%) saturate(1870%) hue-rotate(346deg) brightness(102%) contrast(102%);
    }
  }
`;

const Header = ({ 
  onExpandToggle, 
  expanded,
  allowResizing,
  allowDragging,
  allowFloating,
  onToggleControl,
  isWorkspace = false
}) => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const location = useLocation();
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const navigate = useNavigate();
  const { notifications, unreadCount, clearNotification, addNotification } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);
  const settingsRef = useRef(null);
  const { messages, unreadCount: messageCount, clearMessage, addMessage } = useMessages();
  const [showMessages, setShowMessages] = useState(false);
  const messagesRef = useRef(null);
  const notificationsRef = useRef(null);
  const [isUserApproved, setIsUserApproved] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showUserTooltip, setShowUserTooltip] = useState(false);
  const [companyName, setCompanyName] = useState(null);
  const showFirmStandards = useFeature('firmStandards');

  // Get team member info
  const isMemberAuthenticated = isTeamMemberAuthenticated();
  const memberUser = getTeamUser();

  const isMainPage = location.pathname === '/main';

  // Fetch company name from profile
  useEffect(() => {
    const fetchCompanyName = async () => {
      if (!isAuthenticated) return;
      
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${process.env.REACT_APP_SERVER}profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const profileData = response.data?.profile || response.data;
        const businessName = profileData?.name || profileData?.businessName || profileData?.business_name;
        
        if (businessName) {
          setCompanyName(businessName);
        }
      } catch (error) {
        console.error('Failed to fetch company name:', error);
      }
    };

    fetchCompanyName();
  }, [isAuthenticated, getAccessTokenSilently]);

  useEffect(() => {
    if (isAuthenticated && !user) return;
    
    const fetchOnboardingStatus = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${process.env.REACT_APP_SERVER}onboarding/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        console.log("Onboarding status response:", response.data);
        
        setIsUserApproved(response.data.onboardingStatus === "approved");
        
        console.log("User is approved:", response.data.onboardingStatus === "approved");
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        setIsUserApproved(false);
      }
    };

    if (isAuthenticated) {
      fetchOnboardingStatus();
    }
  }, [isAuthenticated, user, getAccessTokenSilently]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (messagesRef.current && !messagesRef.current.contains(event.target)) {
        setShowMessages(false);
      }
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setShowSettingsMenu(false);
      }
    };

    if (showNotifications || showMessages || showSettingsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications, showMessages, showSettingsMenu]);

  if (!user && !isAuthenticated) {
    return (<div></div>)
  }

  const handleSettingsClick = () => {
    setShowSettingsMenu(!showSettingsMenu);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    setShowSettingsMenu(false);
  };

  const handleNotificationIconClick = () => {
    setShowNotifications(!showNotifications);
    setShowSettingsMenu(false);
    setShowMessages(false);
  };

  const handleNotificationClick = (notification) => {
    clearNotification(notification.threadId);
    setShowNotifications(false);
    
    // Navigate to the relevant thread/conversation
    if (notification.threadId) {
      navigate(`/main/${notification.threadId}`);
    }
  };

  // Check if current user is an admin
  // Admin if: 1) No team members exist (Auth0 user is owner), OR 2) Logged in as team admin
  const adminUserIds = [
    "google-oauth2|118046218869675311812",
    "google-oauth2|106855213778914197229"
  ];
  const isSystemAdmin = user && adminUserIds.includes(user.sub);
  const isTeamAdmin = isMemberAuthenticated && memberUser && memberUser.role === 'admin';
  // A user is considered admin for profile access if they're a team admin or there are no team members
  const canAccessProfile = isTeamAdmin || (!isMemberAuthenticated); // If not logged in as member, the Auth0 user has full access

  const settingsMenuItems = [
    // Only show Profile to admins
    ...(canAccessProfile ? [{
      label: 'Profile',
      icon: <FaUser />,
      onClick: () => navigate('/profile')
    }] : []),
    {
      label: 'Overview',
      icon: <FaChartBar />,
      onClick: () => navigate('/settings/tokens')
    },
    ...(isUserApproved ? [] : [{
      label: 'Onboarding',
      icon: <FaRocket />,
      onClick: () => navigate('/settings/onboarding')
    }]),
    ...(showFirmStandards ? [{
      label: 'Firm Standards',
      icon: <FaCog />,
      onClick: () => navigate('/settings/firm-standards')
    }] : []),
    {
      label: 'Campaigns',
      icon: <FaEnvelope />,
      onClick: () => navigate('/settings/campaigns')
    },
    {
      label: 'Build my agent',
      icon: <FaRocket />,
      onClick: () => navigate('/agent/build')
    },
    {
      label: 'My AI Agents',
      icon: <FaRocket />,
      onClick: () => navigate('/agent')
    },
    {
      label: 'Agent history & reset',
      icon: <FaHistory />,
      onClick: () => navigate('/agent/history')
    },
    {
      label: 'Live check',
      icon: <FaSyncAlt />,
      onClick: () => navigate('/agent/live-check')
    },
    // Only show Admin Panel for the specific system admin user
    ...(isSystemAdmin ? [{
      label: 'Admin Panel',
      icon: <FaLock />,
      onClick: () => navigate('/admin')
    }] : []),
    {
      label: 'Contact us',
      icon: <FaComments />,
      onClick: () => navigate('/contact-us')
    }
  ];

  return (
    <HeaderContainer $isWorkspace={isWorkspace}>
      <LogoContainer>
        <img alt='logo icon' src={Logo} />
      </LogoContainer>

      <CenterControls>
        <ControlButton
          onClick={() => setShowFeedbackModal(true)}
          title="Send Feedback - Report bugs, suggest improvements"
          style={{ 
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
            borderColor: 'rgba(99, 102, 241, 0.4)'
          }}
        >
          <BsBug size={18} color="#8b5cf6" />
        </ControlButton>
        {isMainPage && (
          <>
            <ControlButton
              className={allowResizing ? 'active' : ''}
              onClick={() => onToggleControl('resize')}
              title="Toggle panel resizing"
            >
              <img
                src={ResizeIcon}
                alt="Resize"
                className={allowResizing ? 'enabled' : 'disabled'}
              />
            </ControlButton>
            <ControlButton
              className={allowDragging ? 'active' : ''}
              onClick={() => onToggleControl('drag')}
              title="Toggle panel dragging"
            >
              <img
                src={DragIcon}
                alt="Drag"
                className={allowDragging ? 'enabled' : 'disabled'}
              />
            </ControlButton>
            <ControlButton
              className={allowFloating ? 'active' : ''}
              onClick={() => onToggleControl('float')}
              title="Toggle panel floating"
            >
              <img
                src={FloatIcon}
                alt="Float"
                className={allowFloating ? 'enabled' : 'disabled'}
              />
            </ControlButton>
          </>
        )}
      </CenterControls>

      <RightSection>
        <NotificationIconWrapper 
          ref={notificationsRef} 
          onClick={handleNotificationIconClick}
          title="Notifications"
          style={{ position: 'relative' }}
        >
          <BsBell size={24} color="white" />
          {unreadCount > 0 && (
            <NotificationBadge>{unreadCount}</NotificationBadge>
          )}
          
          {showNotifications && (
            <NotificationDropdown>
              {notifications.length > 0 ? (
                notifications.map((notification, index) => (
                  <NotificationItem 
                    key={notification.threadId || index} 
                    onClick={() => handleNotificationClick(notification)}
                    $type={notification.type}
                  >
                    <div className="icon-container">
                      {notification.type === 'message' ? <BsChat /> : <BsBell />}
                    </div>
                    <div className="content">
                      <div className="title">
                        {notification.type === 'message' 
                          ? `Message from ${notification.sender}` 
                          : notification.title || 'Notification'}
                      </div>
                      <div className="description">
                        {notification.type === 'message' 
                          ? notification.content 
                          : notification.description || notification.content}
                      </div>
                      <div className="time">
                        {new Date(notification.timestamp || notification.date).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </NotificationItem>
                ))
              ) : (
                <NotificationItem>
                  <div className="content">
                    <div className="title">No notifications</div>
                  </div>
                </NotificationItem>
              )}
            </NotificationDropdown>
          )}
        </NotificationIconWrapper>

        <TokenBalanceIndicator />

        <IconContainer
          $clickable={true}
          title="Tutorials"
          onClick={() => window.open('https://www.imavenai.com/tutorials', '_blank', 'noopener,noreferrer')}
        >
          <BsBook size={22} color="#ffffff" />
        </IconContainer>

        <IconContainer 
          ref={settingsRef} 
          $clickable={true} 
          $isOpen={showSettingsMenu}
          style={{ position: 'relative' }}
          title="Settings"
        >
          <img 
            src={SettingIcon} 
            alt="Setting Icon" 
            onClick={() => {
              setShowNotifications(false);
              setShowMessages(false);
              setShowSettingsMenu(!showSettingsMenu);
            }}
          />
          <SettingsSubmenu $isOpen={showSettingsMenu}>
            <Menu>
              {settingsMenuItems.map((item, index) => (
                <MenuItem key={index} onClick={item.onClick}>
                  {item.icon && <span style={{ fontSize: '14px' }}>{item.icon}</span>}
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </SettingsSubmenu>
        </IconContainer>
        <UserInfoContainer 
          onMouseEnter={() => setShowUserTooltip(true)}
          onMouseLeave={() => setShowUserTooltip(false)}
        >
          {/* Show member badge if logged in as team member */}
          {isMemberAuthenticated && memberUser && (
            <MemberBadge>
              <BsPerson />
              {memberUser.name}
              <RoleBadge $role={memberUser.role}>
                {memberUser.role}
              </RoleBadge>
            </MemberBadge>
          )}
          
          <Avatar src={user.picture} alt="User Avatar" />
          
          {/* Tooltip on hover */}
          <UserTooltip $show={showUserTooltip}>
            <TooltipRow>
              <TooltipLabel>Company</TooltipLabel>
              <TooltipValue>{companyName || user?.name || 'Company'}</TooltipValue>
            </TooltipRow>
            {isMemberAuthenticated && memberUser ? (
              <>
                <TooltipRow>
                  <TooltipLabel>Logged in as</TooltipLabel>
                  <TooltipValue>{memberUser.name}</TooltipValue>
                </TooltipRow>
                <TooltipRow>
                  <TooltipLabel>Role</TooltipLabel>
                  <RoleBadge $role={memberUser.role} style={{ marginLeft: 'auto' }}>
                    {memberUser.role}
                  </RoleBadge>
                </TooltipRow>
                <TooltipRow>
                  <TooltipLabel>Email</TooltipLabel>
                  <TooltipValue style={{ fontSize: '11px' }}>{memberUser.email}</TooltipValue>
                </TooltipRow>
              </>
            ) : (
              <TooltipRow>
                <TooltipLabel>User</TooltipLabel>
                <TooltipValue>{user?.email || 'Admin'}</TooltipValue>
              </TooltipRow>
            )}
          </UserTooltip>
        </UserInfoContainer>
      </RightSection>

      <FeedbackModal 
        isOpen={showFeedbackModal} 
        onClose={() => setShowFeedbackModal(false)}
        companyName={companyName}
      />
    </HeaderContainer>
  );
};

export default Header;